/**
 * The User Manual's walkthrough videos — recordings of the real app, made by
 * `e2e/walkthroughs/*.walkthrough.ts`.
 *
 * A recording writes three files: the video and its poster under
 * `public/help/walkthroughs/`, and its step timeline here as JSON. Only the JSON
 * is imported, so the registry knows at build time which walkthroughs exist and
 * a page naming one that was never recorded says so instead of a dead player.
 */

export interface WalkthroughStep {
  /** Seconds from the start of the video. */
  t: number
  title: string
}

export interface Walkthrough {
  name: string
  title: string
  /** Seconds. */
  duration: number
  /** The recording's frame size — a desktop is 1280×800, the mobile app portrait. */
  width: number
  height: number
  steps: WalkthroughStep[]
  videoUrl: string
  posterUrl: string
}

type Timeline = Omit<Walkthrough, 'name' | 'videoUrl' | 'posterUrl' | 'width' | 'height'> & {
  width?: number
  height?: number
}

const files = import.meta.glob<Timeline>('./walkthroughs/*.json', { import: 'default', eager: true })

export function parseWalkthrough(filePath: string, data: Timeline): Walkthrough {
  const name = filePath.replace(/^.*\//, '').replace(/\.json$/, '')
  return {
    name,
    title: data.title,
    duration: data.duration,
    width: data.width ?? 1280,
    height: data.height ?? 800,
    steps: [...data.steps].sort((a, b) => a.t - b.t),
    videoUrl: `/help/walkthroughs/${name}.mp4`,
    posterUrl: `/help/walkthroughs/${name}.jpg`,
  }
}

export const WALKTHROUGHS: Readonly<Record<string, Walkthrough>> = Object.fromEntries(
  Object.entries(files).map(([path, data]) => {
    const w = parseWalkthrough(path, data)
    return [w.name, w]
  }),
)

/** Index of the step playing at `time`: the last one that has started. */
export function stepAt(steps: WalkthroughStep[], time: number): number {
  let i = -1
  for (let k = 0; k < steps.length; k++) {
    if (steps[k].t <= time + 0.05) i = k
    else break
  }
  return Math.max(0, i)
}

/** `75.4` → `1:15`. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
